import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Divider, InputNumber, Loader, Message, Panel, Stack } from 'rsuite';

import API_CONFIG from '../config/apiConfig';
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';

const normalizeBaseUrl = (raw) => String(raw || '').trim().replace(/\/+$/, '');

const ensureApiPrefix = (path) => {
  const p = String(path || '');
  if (!p.startsWith('/')) return `/api/${p}`;
  return p.startsWith('/api/') || p === '/api' ? p : `/api${p}`;
};

const buildApiUrl = (endpoint) => {
  const base = normalizeBaseUrl(API_CONFIG.BASE_URL);
  const path = ensureApiPrefix(endpoint);

  if (!base) return path;

  const baseNoApi = /\/api$/i.test(base) ? base.slice(0, -4) : base;
  if (base.startsWith('/')) {
    if (base === '/api' && path.startsWith('/api')) return path;
    return `${baseNoApi}${path}`;
  }

  return `${baseNoApi}${path}`;
};

export default function EmployeeConnectCloseDatesSamplePage() {
  const authenticatedFetch = useAuthenticatedFetch();
  const nowYear = new Date().getFullYear();

  const [startYear, setStartYear] = useState(nowYear);
  const [yearCount, setYearCount] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const loadCloseDates = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const sy = Number.isInteger(Number(startYear)) ? Number(startYear) : nowYear;
      const yc = Number.isInteger(Number(yearCount)) ? Number(yearCount) : 2;

      const qs = new URLSearchParams();
      qs.set('startYear', String(sy));
      qs.set('yearCount', String(yc));

      const url = buildApiUrl(`/connect/employeeconnect/close-dates?${qs.toString()}`);
      const resp = await authenticatedFetch(url, { method: 'GET' });
      const json = await resp.json().catch(() => null);

      if (!resp.ok) {
        throw new Error(String(json?.message || json?.error || `Request failed (${resp.status})`));
      }

      setPayload(json);
    } catch (err) {
      setError(String(err?.message || err || 'Failed to load close dates.'));
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, nowYear, startYear, yearCount]);

  useEffect(() => {
    void loadCloseDates();
  }, [loadCloseDates]);

  const rows = useMemo(() => (Array.isArray(payload?.data) ? payload.data : []), [payload?.data]);

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>EmployeeConnect Close Dates (Sample)</h3>
      </Stack>

      <Divider />

      <Panel
        bordered
        header="Query"
        style={{ marginBottom: 12 }}
      >
        <Stack spacing={12} alignItems="center" wrap>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Start Year</div>
            <InputNumber
              min={1970}
              max={2200}
              step={1}
              value={startYear}
              onChange={(v) => setStartYear(Number.isFinite(Number(v)) ? Number(v) : nowYear)}
              style={{ width: 120 }}
              disabled={loading}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Year Count</div>
            <InputNumber
              min={1}
              max={5}
              step={1}
              value={yearCount}
              onChange={(v) => setYearCount(Number.isFinite(Number(v)) ? Number(v) : 2)}
              style={{ width: 120 }}
              disabled={loading}
            />
          </div>

          <div style={{ paddingTop: 18 }}>
            <Button appearance="primary" onClick={() => void loadCloseDates()} loading={loading}>
              Refresh
            </Button>
          </div>
        </Stack>
      </Panel>

      {loading ? (
        <Message type="info" showIcon>
          <Loader size="xs" content="Loading close dates from EmployeeConnect…" />
        </Message>
      ) : null}

      {error ? (
        <Message type="error" showIcon>
          {error}
        </Message>
      ) : null}

      {!loading && !error ? (
        <Panel bordered header="Response">
          <div style={{ marginBottom: 10, fontSize: 13 }}>
            <strong>Source:</strong> {String(payload?.source || '—')} &nbsp;|&nbsp;
            <strong>Consumer:</strong> {String(payload?.consumer || '—')} &nbsp;|&nbsp;
            <strong>Count:</strong> {Number(payload?.count ?? rows.length)}
          </div>

          {rows.length === 0 ? (
            <Message type="info" showIcon>
              No close dates returned.
            </Message>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.map((item) => {
                const id = Number(item?.id);
                const name = String(item?.name || `Close Date ${id}`);
                const occurrences = Array.isArray(item?.occurrences) ? item.occurrences : [];

                return (
                  <Panel key={Number.isInteger(id) ? id : name} bordered>
                    <div style={{ fontWeight: 700 }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      Recurrence: {String(item?.recurrence?.type || '—')}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      {occurrences.length === 0 ? (
                        <span style={{ color: '#666' }}>No occurrences in selected year range.</span>
                      ) : (
                        occurrences.map((o) => (
                          <div key={`${o?.year}-${o?.start_date}`}>
                            {o?.year}: {String(o?.start_date || '—')} → {String(o?.end_date || '—')}
                          </div>
                        ))
                      )}
                    </div>
                  </Panel>
                );
              })}
            </div>
          )}
        </Panel>
      ) : null}
    </div>
  );
}
